/**
 * AVLYS EN GODKJENT FRIVILLIG ELLER LEVERANDØR (290.5, migrering 1160).
 *
 * Tre ruter på det samme utvalget som masseoperasjonene: forhåndsvisningen (hvilke vakter som tas av,
 * så bekreftelsen kan vise dem), avlysningen og angren. Én påmelding er et utvalg på én — panelet og
 * lista bruker de samme rutene.
 */
import { z } from 'zod';

import { MAX_BULK_VOLUNTEERS } from './volunteers.js';

const ids = z.array(z.uuid()).min(1).max(MAX_BULK_VOLUNTEERS);

export const cancelVolunteersPreviewRequestSchema = z
  .object({ ids })
  .meta({ id: 'CancelVolunteersPreviewRequest' });
export type CancelVolunteersPreviewRequest = z.infer<typeof cancelVolunteersPreviewRequestSchema>;

/** En vakt som tas av. `personName` er den ansatte når vakta er en leverandøransatts. */
export const cancelledShiftSchema = z
  .object({
    assignmentId: z.uuid(),
    shiftId: z.uuid(),
    title: z.string(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    personId: z.uuid(),
    personName: z.string().nullable(),
  })
  .meta({ id: 'CancelledShift' });
export type CancelledShift = z.infer<typeof cancelledShiftSchema>;

export const cancelVolunteersPreviewItemSchema = z
  .object({
    signupId: z.uuid(),
    displayName: z.string().nullable(),
    /** Firmanavnet når påmeldingen er en leverandørs. */
    supplierName: z.string().nullable(),
    hasEmail: z.boolean(),
    shifts: z.array(cancelledShiftSchema),
  })
  .meta({ id: 'CancelVolunteersPreviewItem' });
export type CancelVolunteersPreviewItem = z.infer<typeof cancelVolunteersPreviewItemSchema>;

export const cancelVolunteersPreviewSchema = z
  .object({
    items: z.array(cancelVolunteersPreviewItemSchema),
    /** De valgte som ikke kan avlyses: ikke godkjent, eller avlyst fra før. */
    skipped: z.number().int().nonnegative(),
  })
  .meta({ id: 'CancelVolunteersPreview' });
export type CancelVolunteersPreview = z.infer<typeof cancelVolunteersPreviewSchema>;

export const cancelVolunteersRequestSchema = z
  .object({
    ids,
    /** «Send e-post om avlysningen» — malen for «Avslått», uten lenken (siden er stengt). */
    notify: z.boolean(),
  })
  .meta({ id: 'CancelVolunteersRequest' });
export type CancelVolunteersRequest = z.infer<typeof cancelVolunteersRequestSchema>;

export const undoVolunteerCancellationRequestSchema = z
  .object({ ids })
  .meta({ id: 'UndoVolunteerCancellationRequest' });
export type UndoVolunteerCancellationRequest = z.infer<
  typeof undoVolunteerCancellationRequestSchema
>;

export const volunteerCancellationResultSchema = z
  .object({
    changed: z.number().int().nonnegative(),
    /** Påmeldingene som faktisk ble endret — det «Angre» i kvitteringen sender tilbake. */
    ids: z.array(z.uuid()),
    /** Vaktene som ble tatt av (avlysning) eller satt tilbake (angre). */
    shifts: z.number().int().nonnegative(),
    /** E-poster lagt i køen. */
    notified: z.number().int().nonnegative(),
  })
  .meta({ id: 'VolunteerCancellationResult' });
export type VolunteerCancellationResult = z.infer<typeof volunteerCancellationResultSchema>;
